import {
  CaretDown,
  CircleNotch,
  FileText,
  Image as ImageIcon,
} from "@phosphor-icons/react";
import { useState } from "react";

import {
  type GetProjectReviewsResponse,
  type ProjectReviewFile,
} from "../../../services/askPunttProjectFeed.types";

/** A small non-interactive file pill shown inside a review bubble. */
function ReviewFileChip({
  file,
  testid,
}: {
  file: ProjectReviewFile;
  testid: string;
}) {
  const isImage = file.type === "image" && file.thumbnailUrl;
  const label = file.name ?? "File";
  return (
    <span
      data-testid={testid}
      className="inline-flex max-w-full items-center gap-1 rounded-md border border-puntt-neutral-gray-5 px-1.5 py-0.5 text-[11px] text-puntt-neutral-gray-11"
    >
      {isImage ? (
        // eslint-disable-next-line jsx-a11y/alt-text -- decorative; name shown as text
        <img
          src={file.thumbnailUrl ?? undefined}
          alt=""
          aria-hidden="true"
          className="size-3.5 shrink-0 rounded object-cover"
        />
      ) : (
        <FileText size={12} aria-hidden="true" className="shrink-0" />
      )}
      <span className="truncate">{label}</span>
    </span>
  );
}

/**
 * Right-hand REVIEWS panel: an overall status chip (e.g. IN REVIEW) plus a
 * card per active asset under review (name + version badge + thumbnail).
 * Clicking a card deep-links into the asset's review/ticket via `onOpenReview`.
 */
export function ProjectReviewsPanel({
  data,
  isLoading = false,
  onOpenReview,
}: {
  data?: GetProjectReviewsResponse;
  isLoading?: boolean;
  onOpenReview?: (item: GetProjectReviewsResponse["reviews"][number]) => void;
}) {
  const [open, setOpen] = useState(true);
  const reviews = data?.reviews ?? [];
  const status = data?.status;

  return (
    <section
      className="shrink-0 overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white"
      aria-label="Reviews"
      data-testid="project-reviews-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Toggle reviews section"
        data-testid="project-reviews-toggle"
        className="flex w-full items-center gap-2 bg-base-white px-4 py-3 text-left"
      >
        <h2 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-12">
          Reviews
        </h2>
        <CaretDown
          size={14}
          aria-hidden="true"
          className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${open ? "" : "-rotate-90"}`}
        />
      </button>

      {open && (
        <div className="border-t border-puntt-neutral-gray-5 px-4 pb-4 pt-3">
          {status && (
            <p
              className="text-puntt-yellow-11 mb-3 text-[11px] font-bold uppercase tracking-wide"
              data-testid="project-reviews-status"
            >
              {status}
            </p>
          )}

          {isLoading ? (
            <div
              className="flex items-center justify-center py-4"
              data-testid="project-reviews-loading"
            >
              <CircleNotch
                size={18}
                weight="bold"
                className="animate-spin text-puntt-neutral-gray-9"
              />
            </div>
          ) : reviews.length === 0 ? (
            <p
              className="text-[13px] leading-relaxed text-puntt-neutral-gray-10"
              data-testid="project-reviews-empty"
            >
              No active reviews. Reviews appear here automatically when a new
              version is uploaded.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {reviews.map((item) => {
                const hasTicket = Boolean(item.ticketId);
                const reviewFiles = item.reviewFiles ?? [];
                const contextAssets = item.contextAssets ?? [];
                const hasChips =
                  reviewFiles.length > 0 || contextAssets.length > 0;
                return (
                  <button
                    key={item.id}
                    type="button"
                    disabled={!hasTicket}
                    aria-disabled={!hasTicket}
                    onClick={() => {
                      if (hasTicket) onOpenReview?.(item);
                    }}
                    aria-label={`Open review for ${item.name}${
                      item.status ? ` (${item.status})` : ""
                    }`}
                    data-testid={`project-review-card-${item.id}`}
                    className="flex flex-col gap-2 rounded-lg border border-puntt-neutral-gray-5 p-3 text-left transition-colors hover:border-puntt-accent-9 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-puntt-neutral-gray-5"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        aria-hidden="true"
                        className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-puntt-red-9 text-base-white"
                      >
                        {item.thumbnailUrl ? (
                          // eslint-disable-next-line jsx-a11y/alt-text -- decorative thumbnail; the card carries an aria-label
                          <img
                            src={item.thumbnailUrl}
                            alt=""
                            aria-hidden="true"
                            className="size-full object-cover"
                          />
                        ) : (
                          <ImageIcon size={18} weight="fill" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-semibold text-puntt-neutral-gray-12">
                          {item.name}
                        </span>
                        {item.versionNumber != null && (
                          <span
                            className="font-mono mt-1 inline-block rounded border border-puntt-neutral-gray-12 bg-puntt-neutral-gray-12 px-1.5 py-px text-[10px] font-medium text-base-white"
                            data-testid={`project-review-card-${item.id}-version`}
                          >
                            v{item.versionNumber}
                          </span>
                        )}
                      </span>
                    </div>
                    {hasChips && (
                      <div className="flex flex-wrap gap-1">
                        {reviewFiles.map((f, idx) => (
                          <ReviewFileChip
                            key={`review-${idx}`}
                            file={f}
                            testid={`project-review-file-${item.id}-${idx}`}
                          />
                        ))}
                        {contextAssets.map((f, idx) => (
                          <ReviewFileChip
                            key={`context-${idx}`}
                            file={f}
                            testid={`project-review-context-${item.id}-${idx}`}
                          />
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
